import { count } from '../core/words.js';
import { behindBody } from '../core/camera.js';
import { MINUTES_PER_DAY } from './clock.js';
import { belowRung } from './standing.js';
import { SUSPENSION_SHIFTS, WRITEUP_COST, describe as describeWriteup } from './writeups.js';

/**
 * What a write-up does, and what several of them do.
 *
 * The paper and the standing hit, the manager's warning, Serg deciding he has
 * carried you far enough, a suspension that costs you shifts and moves the
 * calendar, and the dismissal at the bottom of it. It owns the three latches
 * that make the squeeze a squeeze rather than a loop — the warning that is
 * due, the supervisor who has stopped, the player who is no longer on the
 * roster — and they are saved with everything else.
 *
 * Moved out of `main.js` whole. `elapsed` is asked for, and the day's
 * checkpoint is taken by whoever keeps it.
 */
export function createEscalation(deps) {
  const {
    access,
    applyShortfalls,
    autosave,
    beos,
    carry,
    chapterPanel,
    claims,
    climbing,
    clock,
    concealment,
    favors,
    gateFor,
    ghosts,
    hud,
    impairment,
    level,
    noticed,
    npcs,
    perception,
    player,
    procurement,
    rig,
    setup,
    shift,
    standing,
    stores,
    suspicion,
    takeScreen,
    veil,
    wheeled,
    writeups,
    markDay,
  } = deps;

  /**
   * A write-up: the paper, the standing hit, the warning, and — on the third —
   * the shifts.
   *
   * Everything that can write you up comes through here, so the cooldown, the
   * cost and the strike count cannot disagree about what happened.
   */
  function writeUp(reason, detail, { by = null } = {}) {
    // Nothing is filed against somebody who does not work here.
    if (dismissed) return null;
    // Cooldowns run on summed frame deltas rather than `performance.now()`.
    // In play the two are the same thing; headless they are not, and a rule the
    // test harness cannot advance past is a rule the test harness cannot test.
    const entry = writeups.add(reason, {
      detail,
      day: clock.day,
      at: clock.minutes,
      now: deps.elapsed() * 1000,
    });
    if (!entry) return null;

    hud.say(
      `WRITTEN UP (${entry.index}/${writeups.max}): ${describeWriteup(reason)}. Clears day ${entry.clears}.`,
      { tone: 'bad', seconds: 6 }
    );
    /*
     * A write-up is Management noticing you, and your own department wearing it.
     *
     * `by` is the department that filed it, when one did. It is held out of the
     * ripple's upside for the length of this, because the matrix pays a
     * department's rivals when it falls — and the reporter is one of Management's
     * rivals. Without it, being turned in by the Front Desk made the Front Desk
     * think better of you. A deadline missed or a room not set has no reporter
     * and passes nothing, which is right: nobody turned you in.
     */
    standing.applyAll(WRITEUP_COST, { reason: null, noGain: by ? [by] : [] });

    readTheRoom();
    armWarning();
    autosave.touch();
    if (entry.index >= writeups.max) suspend();
    return entry;
  }

  /**
   * The second strike arms your manager, and nothing else does.
   *
   * Armed rather than delivered, because the whole point is that he comes to you
   * — he could be two hundred feet away with a cart between you when this fires,
   * and a warning that teleports is a notification with a face drawn on it.
   * `interceptWarning` in the frame loop is the other half.
   *
   * Saved, so closing the tab between the strike and the conversation does not
   * cancel the conversation. Re-armed by the count coming back up to two, which
   * is what makes a second suspension arrive with its own warning rather than
   * with the first one's.
   */
  let warningDue = false;

  function armWarning() {
    const rule = gateFor('management')?.warns;
    if (!rule) return;
    // Disarmed as well as armed. A card that ages back down to one line is not a
    // card anybody is coming to talk to you about, and a manager who turns up to
    // warn you about a write-up that expired last night is a bug with dialogue.
    if (writeups.count < rule.at) warningDue = false;
    else if (writeups.count === rule.at) warningDue = true;
  }

  /**
   * Your supervisor, and the moment he stops carrying you.
   *
   * Everything else in this game degrades: a point here, six points there, and
   * you can watch it happen and decide what to do about it. He does not. He
   * covers for you silently, which is indistinguishable from nothing happening,
   * and past a certain number of things on your card inside a few days he goes
   * down a whole step at once — no warning, because a warning is the start of a
   * negotiation and the whole character is somebody who does not have that
   * conversation with you.
   *
   * Latched, and the latch is saved. `floorTo` only ever lowers, so a rule that
   * re-fired on every write-up after the third — or on every reload — would walk
   * a department down the ladder a step at a time, which is the degrading it
   * exists not to do. It re-arms when the incidents age out of the window, which
   * is him deciding you have had a quiet fortnight.
   */
  let supervisorStopped = false;

  function readTheRoom() {
    const gate = gateFor('banquets');
    const rule = gate?.covers;
    if (!rule) return null;
    const since = clock.day - (rule.withinDays ?? 3) + 1;
    const recent = writeups.entries.filter((file) => (file.day ?? 0) >= since).length;

    if (recent < (rule.incidents ?? 3)) {
      supervisorStopped = false;
      return null;
    }
    if (supervisorStopped) return null;
    supervisorStopped = true;

    const event = standing.floorTo(gate.department, belowRung(standing.get(gate.department)), {
      reason: `${gate.name} has stopped covering for you`,
    });
    if (event.changes.length) {
      /*
       * One sentence and then the board. It is not a warning and it does not
       * explain anything, because the whole character is somebody who will not
       * have that conversation with you; what it does is name the thing `assigns`
       * on his row has already done to what you will be given tomorrow.
       */
      const him = npcs.find((candidate) => candidate.data.id === gate.id);
      if (gate.stops) him?.say(gate.stops, 8);
      hud.say(
        gate.stops
          ? `${gate.name}: "${gate.stops}" Then he goes back to the board.`
          : `${gate.name} does not say anything, which is the point.`,
        { tone: 'bad', seconds: 7 }
      );
    }
    return event;
  }

  /**
   * Three strikes: you lose shifts, not the day.
   *
   * This used to reload the start of the day, and a reload is not a consequence —
   * it is an undo with a stern message attached. Nothing that happened to the
   * hotel happened, nobody's opinion moved, and the correct way to play was to
   * collect two strikes and then take any risk at all, because the third one
   * handed the morning back.
   *
   * So the clock goes *forward* instead. You are off the roster for a fortnight's
   * worth of shifts; the property runs them without you, which means every event
   * on every board you are not there for resolves the way any event nobody sets
   * resolves, every debt called in while you were gone goes unanswered, and every
   * stock count you were hoping nobody would do gets done. You come back to a
   * building that has had a bad week and has an opinion about whose fault that
   * was.
   *
   * Strikes reset. Standing does not, and that asymmetry is the whole of it: you
   * have paid for the write-ups, and what you paid with is where you stand.
   */
  function suspend() {
    const penalty = writeups.nextSuspension();
    if (penalty.dismissed) return dismiss();

    const lost = penalty.shifts;
    // The shifts you lose are the next `lost` of them, and the day you come back
    // is the one after that. Off by one here is the difference between a two
    // shift suspension and a one shift suspension with a stern letter.
    const from = clock.day + 1;
    const until = clock.day + lost;
    const back = until + 1;

    /*
     * Put down whatever is in your hands before the clock moves.
     *
     * The accepted event is abandoned rather than closed, so it sweeps as a ghost
     * with the rest of its day: it went ahead without you like everything else
     * did, and filing a grade for a room you were escorted out of would be the
     * building marking its own homework.
     */
    beos.abandon();
    setup.clear();
    carry.clear();
    stores.close();
    wheeled.reset();
    climbing.reset();
    applyShortfalls();

    /*
     * The rest of today, first and explicitly.
     *
     * You are escorted out at four in the afternoon and the six o'clock events
     * still happen. The board would have been swept anyway the next time the day
     * rolled over — but that sweep lands on the far side of the save, so a player
     * who closed the tab on the suspension screen came back to five rooms burning
     * that had not been burning when they left. Resolve it here, where the
     * standing it moves is the standing the notice is about to report.
     */
    beos.sweepDay(clock.day);

    const missed = runShiftsWithout(from, until);
    // And back on the roster, at the minute the first shift you are allowed to
    // work opens.
    wakeOn(back);

    // The card is wiped and the tally is not. `serve` is the only thing that
    // knows the difference, and it is the thing the terminal state counts.
    writeups.serve();
    warningDue = false;
    // A fresh window on a fresh card: whether he is still holding Banquets back
    // is a different question on the day you come back.
    readTheRoom();

    for (const npc of npcs) npc.reset();
    player.teleport({ x: level.spawn.x, y: level.spawn.y, z: level.spawn.z });
    player.yaw = level.spawn.yaw;
    rig.yaw = behindBody(level.spawn.yaw);
    perception.clear();
    suspicion.clear();
    concealment.clear();
    impairment.clear();
    claims.clear();
    access.clear();
    access.settle();

    markDay();
    autosave.touch();
    showSuspension({ penalty, lost, from, back, missed });
    return penalty;
  }

  /**
   * Runs the days you are not there for, one at a time, and says what happened.
   *
   * One at a time because every one of them is a separate board with its own
   * deadlines, and `beos.update` only ever sweeps the day it last saw — skipping
   * from Monday to Friday in one jump would quietly forgive three nights.
   *
   * The order inside a day is the order the day happens in: the rooms go ahead
   * without you, anybody still working up to saying something says it or drops
   * it, the debts you owed are left standing, and the stock gets counted
   * overnight.
   */
  /**
   * Wakes up on a named day, at the minute that day's clock opens.
   *
   * `clock.advanceTo` takes a time of day, not a date — it is built for sleeping,
   * where "tomorrow" is whichever midnight comes next. Ask it for a Thursday
   * morning from a Tuesday afternoon and it gives you Tuesday evening, which for
   * a suspension is the difference between losing two shifts and losing one. So
   * the gap is worked out in absolute minutes and handed over as the distance to
   * travel, which is the one thing that module cannot get wrong.
   */
  function wakeOn(day) {
    const opens = shift.windowFor(day).opens;
    const gap = (day - clock.day) * MINUTES_PER_DAY + opens - clock.minutes;
    clock.advanceTo(opens, { atLeast: Math.max(1, gap) });
    return clock.day;
  }

  function runShiftsWithout(from, to) {
    const missed = { events: 0, ghosts: [], favors: 0, counted: 0, days: [] };

    for (let day = from; day <= to; day += 1) {
      const before = ghosts.resolved.length;
      // To the moment that day's shift would have opened, so each board is swept
      // from inside its own day rather than from a week later.
      wakeOn(day);
      beos.sweepDay(day);
      const raised = ghosts.forDay(day);
      missed.events += ghosts.resolved.length - before;
      missed.ghosts.push(...raised);

      noticed.closeOut();
      // Told the date without being allowed to interrupt anybody: the ledger
      // works out when a debt is overdue from the last day it was shown, and a
      // fortnight of days it never saw is a fortnight it thinks did not happen.
      favors.update(clock, { canInterrupt: false });

      /*
       * A debt called in to somebody who is not on the property.
       *
       * Refused rather than quietly deferred, and refused as *expired*, because
       * that is exactly what it is from the other end: they went to find you, you
       * were not there, and nobody in this building has ever accepted "he was
       * suspended" as the reason their thing did not get done.
       */
      for (const favor of favors.owed) {
        if (favor.callDay > day) continue;
        favors.refuse(favor.id, { expired: true });
        missed.favors += 1;
      }

      missed.counted += procurement.settlePending(day + 1).length;
      missed.days.push(day);
    }

    return missed;
  }

  /**
   * The floor, and the only thing in this game you cannot work your way out of.
   *
   * Reachable on purpose, and long on purpose. Nine write-ups — three full sets —
   * and because the second of every set is the one your manager crosses the
   * property to talk to you about, three separate conversations in which the
   * number and the dates were said out loud. Nobody arrives here by surprise.
   *
   * It is the one place the old reload survives, inverted: there is no day to go
   * back to and the week is over. The only thing on the other side of it is being
   * somebody else, which is the button that was always on the veil.
   */
  function dismiss() {
    dismissed = true;
    showDismissal();
    return { number: SUSPENSION_SHIFTS.length + 1, shifts: 0, dismissed: true };
  }

  /**
   * Whether you still work here.
   *
   * A latch, and saved with everything else. Its only job is that nothing further
   * is filed: without it a dismissed player walking through the lobby collects a
   * fourth and a fifth write-up, the card reads "5 of 3", and the end screen
   * reappears every twenty-five seconds. You are not on the roster, so there is
   * nobody to write up.
   */
  let dismissed = false;

  /**
   * What the days you were not there did to the place, in a screen you cannot
   * dismiss without reading.
   *
   * The undismissable part is the argument. Everything on it happened while the
   * game was not running for you, so it is the one cost in this game the player
   * has no chance of having watched — and a cost you never see is a cost that
   * teaches nothing. The numbers are counted from what actually resolved rather
   * than from the length of the suspension, so a quiet fortnight reads as a quiet
   * fortnight.
   */
  function showSuspension({ penalty, lost, from, back, missed }) {
    const worst = missed.ghosts.filter((entry) => 'DF'.includes(entry.letter)).length;
    const lines = [
      `${count(lost, 'shift')}. ${lost === 1 ? `Day ${from}` : `Day ${from} to day ${back - 1}`}. Do not come in.`,
      missed.events
        ? `${missed.events === 1 ? 'An event' : `${missed.events} events`} went ahead without you${worst ? (missed.events === 1 ? ', badly' : `, ${worst} of them badly`) : ''}.`
        : 'The board was quiet. You were lucky in the one way you could be.',
      missed.favors
        ? `${missed.favors === 1 ? 'Somebody' : `${missed.favors} people`} came looking for what you owed them and you were not here.`
        : 'Nobody came looking for you, which is its own answer.',
      missed.counted
        ? `And somebody finally counted the stock. ${missed.counted === 1 ? 'One of them did.' : `${missed.counted} of them did.`}`
        : 'Your card is clear. Where you stand is not, and that is the part you keep.',
    ];

    takeScreen();
    chapterPanel.show(
      {
        id: `suspension-${penalty.number}`,
        kind: 'suspension',
        kicker: penalty.number > 1 ? `Suspension ${penalty.number}` : 'Suspended',
        title: 'Off the roster',
        speaker: gateFor('management')?.name ?? 'Management',
        done: 'Come back in',
        lines,
      },
      {
        onDone: () => {
          veil.suppress(false);
          veil.show();
          hud.say(
            `Day ${clock.day}. Back on the roster with a clean card and the same reputation.`,
            { tone: 'warn', seconds: 7 }
          );
          autosave.touch();
        },
      }
    );
  }

  /**
   * The end of the line.
   *
   * It does not reset anything and it does not offer to. The week stays exactly
   * where it is, on disk, with nine write-ups and two served suspensions in it,
   * because a dismissal that quietly hands you a fresh Monday is a loading
   * screen. Starting again is a button on the veil and it always was, and
   * pressing it is the player deciding to be somebody else rather than the game
   * deciding for them.
   */
  function showDismissal() {
    takeScreen();
    chapterPanel.show(
      {
        id: 'dismissed',
        kind: 'dismissal',
        kicker: 'Dismissed',
        title: 'That is the third time',
        speaker: gateFor('management')?.name ?? 'Management',
        done: 'Hand in the uniform',
        lines: [
          'Two suspensions and you are back on three again. There is no fourth conversation.',
          'I told you the number twice. I told you the dates twice.',
          'Leave the uniform in the break room. Somebody will walk you out.',
          'If you want to work in this building again you will be somebody else. Start the week again on the pause screen.',
        ],
      },
      {
        onDone: () => {
          veil.suppress(false);
          veil.show();
          autosave.touch();
        },
      }
    );
  }

  return {
    writeUp,
    armWarning,
    readTheRoom,
    suspend,
    /** The manager's crossing is owed. */
    get warningDue() {
      return warningDue;
    },
    /** It has been had. */
    warningHeard() {
      warningDue = false;
    },
    /** Serg has stopped carrying you. */
    get supervisorStopped() {
      return supervisorStopped;
    },
    /** Not on the roster any more. */
    get dismissed() {
      return dismissed;
    },
    toJSON: () => ({ supervisorStopped, dismissed, warningDue }),
    load(state) {
      supervisorStopped = Boolean(state?.supervisorStopped);
      dismissed = Boolean(state?.dismissed);
      warningDue = Boolean(state?.warningDue);
    },
    clear() {
      supervisorStopped = false;
      warningDue = false;
      dismissed = false;
    },
  };
}
